import { FC, useContext, useState } from "react";
import { useParams } from "react-router-dom";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext } from "@/app/providers";
import { UserCompanyConfigurationContext, TranslationContext } from "@/app/providers";
import { useUpdateStepStatus } from "@/features/procedures/api/checklist.mutations";
import { useFetchChecklistStepStatuses } from "@/features/procedures/api/checklist.queries";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import {
	StepStatus,
	UpdateStepStatusRequest,
} from "@/features/procedures/model/types/checklist";
import { CheckpointType } from "@/features/procedures/model/types/manualChecklist";
import { ProcedureNodeStepStatusTypes } from "@/features/procedures/model/types/node";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { RemoveStepCheckedModal } from "@/features/procedures/ui/components/modals/RemoveStepCheckedModal";
import { Tooltip } from "@mui/material";

import StatusCheckedIcon from "@/assets/svg/interactions/icon-status-checked.svg?react";
import StatusUncheckedIcon from "@/assets/svg/interactions/icon-status-unchecked.svg?react";

import {
	UserCompanyConfigurationKeys as ConfigKeys,
	GetUserCompanyConfigurationToggle,
} from "@/model/user-company-configuration/UserCompanyConfiguration";

import {
	checkAdminAccess,
	checkGroupAccess,
} from "@/service/ApplicationQueries";
import { RouteParams } from "@/service/Utilities";
import { Language } from "@/model/Language/Language";


interface ChecklistStatusProps {
	checklistId: number | undefined;
	step: ProcedureNodeStep;
}

export const ChecklistStatus: FC<ChecklistStatusProps> = ({
	step,
	checklistId,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	const [isSaving, setIsSaving] = useState(false);
	const [showRemoveCheckedStepModal, setShowRemoveCheckedStepModal] =
		useState(false);
	const updateProcedureDetailStepState = useUpdateStepStatus();
	const { isLoading, data: stepStatuses } = useFetchChecklistStepStatuses(
		checklistId,
		procedureId,
	);

	const user = useContext(UserContext);

	const CloseRemoveStepCheckedModal = () => {
		setShowRemoveCheckedStepModal(false);
	};

	const userCompanyConfiguration = useContext(UserCompanyConfigurationContext);

	const forceComment = () => {
		return GetUserCompanyConfigurationToggle(
			ConfigKeys.ForceComments,
			userCompanyConfiguration,
		);
	};

	const Save = async (effectiveStatus: StepStatus) => {
		if (isSaving) {
			return;
		}
		if (effectiveStatus.readonly || step.Readonly || effectiveStatus.Disabled) {
			return;
		}

		setIsSaving(true);
		let postValueToServer = false;
		let newStatus: ProcedureNodeStepStatusTypes = 0;

		try {
			if (!effectiveStatus.Status) {
				newStatus = 1;
				postValueToServer = true;
			} else if (effectiveStatus.Status) {
				if (forceComment()) {
					setShowRemoveCheckedStepModal(true);
				} else {
					postValueToServer = true;
				}
			}

			if (postValueToServer) {
				const request: UpdateStepStatusRequest = {
					ArchiveType: "Status",
					StepId: step.Id,
					ProcedureId: parseInt(id ?? "0"),
					SchemeId: checklistId,
					Status: newStatus,
					SavedTagValue: undefined,
					Metadata: { ...step, Status: newStatus },
				};

				await updateProcedureDetailStepState(request);
			}
		} finally {
			setIsSaving(false);
		}
	};

	if (
		step?.AccessLevel === "Locked" ||
		step.CheckpointType === CheckpointType.SATOSLive ||
		step.CheckpointType === CheckpointType.Info
	) {
		return <></>;
	}

	const getStepStatus = (
		step: ProcedureNodeStep,
		statuses: StepStatus[] | undefined,
	): StepStatus | undefined => {
		if (step && Array.isArray(statuses)) {
			const intstatus = statuses.find((s) => s.Id === step.Id);
			if (!intstatus) return undefined;

			let readonly = false;
			let disabled = intstatus.Disabled ?? false;
			if (step?.GroupId > 0 && user) {
				if (
					!checkGroupAccess(user.Groups, [step?.GroupId]) &&
					!checkAdminAccess(user)
				) {
					readonly = true;
					if (!intstatus.Status) disabled = true;
				}
			}
			return { ...intstatus, readonly, Disabled: disabled };
		}
		return undefined;
	};

	// Use enriched step data when statuses still loading—buttons functional immediately
	const fallbackStatus: StepStatus = {
		Id: step.Id,
		Status: !!step.Status,
		Disabled: false,
		readonly: !!(
			step.GroupId > 0 &&
			user &&
			!checkGroupAccess(user.Groups, [step.GroupId]) &&
			!checkAdminAccess(user)
		),
		IsSkipped: step.IsSkipped ?? false,
		IsOverridden: step.IsOverridden ?? false,
		ListenerTags: [],
		ManualMeasurementValue: undefined,
		ManualMeasurementTimeStamp: undefined,
	};
	if (fallbackStatus.readonly && !step.Status) fallbackStatus.Disabled = true;

	const status = getStepStatus(step, stepStatuses) ?? fallbackStatus;

	const touchTargetSx = {
		minWidth: 36,
		minHeight: 36,
		width: 36,
		height: 36,
		padding: 0.75,
		margin: 0.5,
		borderRadius: 2,
		boxShadow: (t: any) =>
			t.palette.mode === "light"
				? "0 1px 3px rgba(0,0,0,0.12)"
				: "0 1px 3px rgba(0,0,0,0.3)",
		"&:focus-visible": {
			outline: "2px solid",
			outlineColor: "primary.main",
			outlineOffset: 2,
		},
		"&:active": { transform: "scale(0.97)" },
	};

	if (
		status.Disabled ||
		(step.CheckpointType === CheckpointType.ManualMeasurement && !status.Status)
	) {
		return (
			<Tooltip
				title={
					<Translate String="Checklist.Step.Tooltip.NotAvailable">
						Step not available
					</Translate>
				}
				arrow
			>
				<span>
					<ProcedureButton
						variant="contained"
						color="error"
						disabled={true}
						type="button"
						aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.ChecklistStatus.L203C18.StepNotAvailable", "Step not available")}
						sx={touchTargetSx}
					>
						<StatusUncheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
					</ProcedureButton>
				</span>
			</Tooltip>
		);
	} else if (status.Status) {
		return (
			<>
				<Tooltip
					title={
						<Translate String="Checklist.Step.Tooltip.MarkNotDone">
							Mark as not done
						</Translate>
					}
					arrow
				>
					<ProcedureButton
						variant="contained"
						color="success"
						disabled={step.IsSkipped || isSaving}
						onClick={() => {
							void Save(status);
						}}
						type="button"
						aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.ChecklistStatus.L230C18.MarkAsNotDone", "Mark as not done")}
						sx={touchTargetSx}
					>
						<StatusCheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
					</ProcedureButton>
				</Tooltip>
				<RemoveStepCheckedModal
					activeSchemeId={checklistId}
					show={showRemoveCheckedStepModal}
					step={step}
					CloseModal={CloseRemoveStepCheckedModal}
				/>
			</>
		);
	} else {
		return (
			<Tooltip
				title={
					<Translate String="Checklist.Step.Tooltip.MarkDone">
						Mark as done
					</Translate>
				}
				arrow
			>
				<ProcedureButton
					variant="contained"
					color="error"
					disabled={step.IsSkipped || isSaving}
					onClick={() => {
						void Save(status);
					}}
					type="button"
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.ChecklistStatus.L262C17.MarkAsDone", "Mark as done")}
					sx={touchTargetSx}
				>
					<StatusUncheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
				</ProcedureButton>
			</Tooltip>
		);
	}
};
